import React, { useEffect, useState, useRef } from 'react';
import { ENV, SITE_URL } from '../../../config/index.js';

// ux
import FuseSlider from '../ux/FuseSlider';
import FuseHelpModal from '../ux/FuseHelpModal.js';
import LoadingAnimationSimple from '../ux/LoadingAnimationSimple';

const Launchpad = ({
  inputs,
  handleInputsChange,
  currentQuestion,
  handleQuestionChange,
  handleCreateProject,
  helpOpen,
  toggleHelp
}) => {
  const [contextInFocus, setContextInFocus] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [currentWelcomeStep, setCurrentWelcomeStep] = useState(1);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [welcomeVideoUrl, setWelcomeVideoUrl] = useState('');

  const ideaTextareaRef = useRef(null);
  const contextTextareaRef = useRef(null);

  // Adjust height of textareas on content change
  useEffect(() => {
    autoResize(ideaTextareaRef.current);
  }, [inputs.idea]);

  useEffect(() => {
    autoResize(contextTextareaRef.current);
  }, [inputs.context]);

  useEffect(() => {
    const fetchWelcomeVideoUrl = async () => {
      try {
        // Fetch the custom options endpoint
        const response = await fetch(`${SITE_URL}/wp-json/custom/v1/options`);
        const data = await response.json();
        if (data && data.welcome_video_url) {
          setWelcomeVideoUrl(data.welcome_video_url);
        } else {
          console.warn('ACF field "welcome_video_url" not found.');
        }
      } catch (error) {
        console.error('Failed to fetch ACF welcome video URL:', error);
      }
    };

    fetchWelcomeVideoUrl();
  }, []);

  const autoResize = (textarea) => {
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  // tutorial functions
  const totalSteps = 3;
  const nextHelpStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep((prevStep) => prevStep + 1);
    } else {
      endHelpTutorial();
    }
  };

  const prevHelpStep = () => {
    if (currentStep > 1) {
      setCurrentStep((prevStep) => prevStep - 1);
    }
  };

  const endHelpTutorial = () => {
    toggleHelp();
    setCurrentStep(1);
  };

  // welcome functions
  const totalWelcomeSteps = 2;

  const nextWelcomeStep = () => {
    setCurrentWelcomeStep((prevStep) => {
      if (prevStep < totalWelcomeSteps) {
        return prevStep + 1;
      } else {
        endWelcomeTutorial();
        return prevStep;
      }
    });
  };

  const prevWelcomeStep = () => {
    setCurrentWelcomeStep((prevStep) =>
      prevStep > 1 ? prevStep - 1 : prevStep
    );
  };

  // Check if welcome modal should be shown
  useEffect(() => {
    const modalShown = localStorage.getItem('welcomeModalShown');
    if (!modalShown) {
      setWelcomeOpen(true); // Show modal if not shown before
    }
  }, []);

  const endWelcomeTutorial = () => {
    setWelcomeOpen(false);
    setCurrentWelcomeStep(1);
    localStorage.setItem('welcomeModalShown', 'true'); // Set flag to avoid showing again
  };

  return (
    <div className="fuselight-launchpad-container">
      <div className="launchpad-bg">
        <div></div>
        <div></div>
        <div></div>
      </div>
      {helpOpen && (
        <div className={`launchpad-help-modal-container step-${currentStep}`}>
          <FuseHelpModal
            classes="fuse-help-modal help-modal-1 mobile-help-modal-2"
            clickClose={endHelpTutorial}
            clickNext={nextHelpStep}
            clickPrev={prevHelpStep}
            currentStep={currentStep}
            totalSteps={totalSteps}
          >
            <div className="mobile-help-content">
              <img
                src={
                  ENV === 'development'
                    ? `${SITE_URL}/app/themes/hahn/images/logo/FuseLightLogo.svg`
                    : `${SITE_URL}/wp-content/themes/hahn/images/logo/FuseLightLogo.svg`
                }
                alt="Fuselight Logo Icon"
                className="header-logo-icon"
                width={30}
              />
              <div className="button-caret-mobile">
                <div></div>
                <div></div>
              </div>
              <p>Click on the icon to open the side navigation</p>
            </div>
            <h4>Launchplans</h4>
            <p>
              Any brand fusion ideas you have entered will appear here, in your
              Launchplans. You can always go back and sort through them, adjust,
              or ignite them into their own fusion. You can also automatically
              create a pitch deck for up to three fusions at once from this
              menu.
            </p>
            <h4>Fusions</h4>
            <p>
              Click here to view the fusion boards for any and all of your past
              brand fusions. You can adjust and experiment by using the{' '}
              <strong style={{ color: '#00edff' }}>Ask me anything</strong> chat
              window.
            </p>
            <h4>Pitches</h4>
            <p>
              Search, sort and share any and all past pitches or presentation
              decks you’ve created directly from here.
            </p>
          </FuseHelpModal>

          <FuseHelpModal
            classes="fuse-help-modal help-modal-2"
            clickClose={endHelpTutorial}
            clickNext={nextHelpStep}
            clickPrev={prevHelpStep}
            currentStep={currentStep}
            totalSteps={totalSteps}
          >
            <p>
              Worry not, brand voyager. All of your launchplans, brand fusions,
              and pitches are stored here.
            </p>
            <p>Click the arrow to expand and collapse this menu at any time.</p>
          </FuseHelpModal>
          <FuseHelpModal
            classes="fuse-help-modal help-modal-3 mobile-help-modal-1"
            clickClose={endHelpTutorial}
            clickNext={nextHelpStep}
            clickPrev={prevHelpStep}
            currentStep={currentStep}
            totalSteps={totalSteps}
          >
            <div className="mobile-help-content">
              <label>Who are you promoting?</label>
              <div className="blue-line"></div>
              <div className="plus-icon"></div>
              <label>Who do you want to partner with?</label>
              <div className="blue-line"></div>
            </div>
            <p>
              Enter the name of the brand/celebrity/ event/idea you want to
              promote.
            </p>
            <p>
              Next, enter the partner you have in mind. If you don’t have one,
              no worries. Just click{' '}
              <strong style={{ color: '#00edff' }}>Need ideas</strong> and we’ll
              give you some thought-starters.
            </p>
            <p>
              Follow the prompts. You may be asked other optional questions
              based on your responses.
            </p>
          </FuseHelpModal>
        </div>
      )}
      {welcomeOpen && (
        <div
          className={`launchpad-help-modal-container welcome-step-${currentWelcomeStep}`}
        >
          <div className="welcome-modal">
            <button className="close" onClick={endWelcomeTutorial}></button>
            {currentWelcomeStep === 1 && (
              <div className="welcome-modal-content-1">
                {welcomeVideoUrl ? (
                  <video autoPlay loop muted>
                    <source src={welcomeVideoUrl} type="video/mp4" />
                  </video>
                ) : (
                  <div className="loading-container">
                    <LoadingAnimationSimple />
                  </div>
                )}
                <h4>Welcome to your new collaboration engine.</h4>
                <p>
                  Learn how to find the most powerful partnerships possible with
                  our quick tour.
                </p>
              </div>
            )}
            {currentWelcomeStep === 2 && (
              <div className="welcome-modal-content-2">
                <h3>Tools to help you.</h3>
                <h4>Quick help.</h4>
                <p>
                  Click on <span className="help-icon">?</span> to get instant
                  help on any page.
                </p>
                <h4>
                  Using FuseLight<sup>SM</sup>.
                </h4>
                <p>
                  Click on “How it works” in the side navigation bar to get an
                  overview.
                </p>
                <h4>Definitions.</h4>
                <p>
                  Hover on any tab in the sidebar on the left for a quick
                  explanation of the term/item.
                </p>
              </div>
            )}
            <div className="nav-buttons">
              {currentWelcomeStep > 1 && (
                <button className="prev" onClick={prevWelcomeStep}>
                  &larr;
                </button>
              )}
              {currentWelcomeStep < totalWelcomeSteps && (
                <button className="next" onClick={nextWelcomeStep}>
                  &rarr;
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="launchpad-input-container">
        <h1 className={currentQuestion.questionOne ? 'visible' : 'answered'}>
          Let's start your brand fusion.
        </h1>
        <div className="question-container">
          <div
            className={
              currentQuestion.questionOne
                ? 'question question-one visible'
                : 'question question-one answered'
            }
          >
            <div
              className={
                inputs.brand_name
                  ? 'question question-one-a answered'
                  : 'question question-one-a '
              }
            >
              <label htmlFor="brand_name">Who are you promoting?</label>
              <input
                type="text"
                name="brand_name"
                value={inputs.brand_name}
                onChange={handleInputsChange}
              />
            </div>
            <div
              className={
                inputs.brand_name ? 'plus-icon visible' : 'plus-icon hidden'
              }
            ></div>
            <div
              className={
                inputs.brand_name
                  ? inputs.celeb_name
                    ? 'question question-one-b visible answered'
                    : 'question question-one-b visible'
                  : 'question question-one-b hidden'
              }
            >
              <label htmlFor="celeb_name">
                Who do you want to partner with?
              </label>
              <input
                type="text"
                name="celeb_name"
                value={inputs.celeb_name}
                onChange={handleInputsChange}
              />
            </div>
            <div
              className={
                inputs.brand_name
                  ? 'question-one-buttons visible'
                  : 'question-one-buttons hidden'
              }
            >
              {inputs.brand_name && !inputs.celeb_name && (
                <div className="ideas-button">
                  <button
                    className="btn btn--tertiary"
                    onClick={() => handleQuestionChange('questionThree')}
                  >
                    <img
                      src={
                        ENV === 'development'
                          ? `${SITE_URL}/app/themes/hahn/images/icons/chat-icon.svg`
                          : `${SITE_URL}/wp-content/themes/hahn/images/icons/chat-icon.svg`
                      }
                      alt="Message Icon"
                      className="message-icon"
                      width={30}
                    />
                    <span>Need ideas?</span>
                  </button>
                </div>
              )}
              {inputs.brand_name && inputs.celeb_name && (
                <div className="next-button">
                  <button
                    className="btn btn--primary"
                    onClick={() => handleQuestionChange('questionTwo')}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>

          <div
            className={
              currentQuestion.questionTwo
                ? inputs.idea
                  ? 'question question-two visible input-answered'
                  : 'question question-two visible'
                : currentQuestion.questionThree
                ? 'question question-two answered input-answered'
                : 'question question-two hidden'
            }
          >
            <label htmlFor="idea">
              Do you have an idea for this brand fusion?
            </label>
            <textarea
              ref={ideaTextareaRef}
              type="text"
              name="idea"
              value={inputs.idea}
              onChange={handleInputsChange}
            />
            <div className="buttons-row">
              {!inputs.idea ? (
                <div className="skip-button">
                  <button
                    className="btn btn--primary-outline"
                    onClick={() => handleQuestionChange('questionThree')}
                  >
                    Skip
                  </button>
                </div>
              ) : (
                <button
                  className="btn btn--secondary-outline"
                  onClick={handleCreateProject}
                >
                  Launch
                </button>
              )}
            </div>
          </div>
          <div
            className={
              currentQuestion.questionThree
                ? inputs.context
                  ? 'question question-three visible answered'
                  : 'question question-three visible'
                : 'question question-three hidden'
            }
          >
            <label htmlFor="context" className="with-tooltip">
              Would you like to tell me more about what you're are trying to
              accomplish?
              <div className="launchpad-tooltip">
                <div className="tooltip-icon">i</div>
                <p>
                  Any context you provide (for instance, the audience you want
                  to reach or the promotion you are considering) will help
                  create better suggestions.
                </p>
              </div>
            </label>

            <textarea
              ref={contextTextareaRef}
              name="context"
              value={inputs.context}
              onChange={handleInputsChange}
              onFocus={() => setContextInFocus(true)}
            />
            {!inputs.context && !contextInFocus && (
              <div className="question-three-placeholders">
                <p>
                  We want to get Gen Z excited about our limited time offer.
                </p>
                <p>
                  We want to reach pet parents and show them our new line of
                  organic pet toys.
                </p>
                <p>
                  We are trying to reach people on the west coast to promote our
                  active gear.
                </p>
              </div>
            )}

            <div className="buttons-row">
              {inputs.context ? (
                <button
                  onClick={handleCreateProject}
                  className="btn btn--secondary-outline"
                >
                  Generate Ideas
                </button>
              ) : (
                <button
                  onClick={handleCreateProject}
                  className="btn btn--primary-outline"
                >
                  Skip
                </button>
              )}
            </div>
          </div>
        </div>
        {currentQuestion.questionTwo && (
          <button
            onClick={() => handleQuestionChange('questionOne')}
            className="question-two-back-button back-button"
          ></button>
        )}
        {currentQuestion.questionThree && (
          <>
            {!inputs.celeb_name ? (
              <button
                onClick={() => handleQuestionChange('questionOne')}
                className="question-three-back-button back-button"
              ></button>
            ) : (
              <button
                onClick={() => handleQuestionChange('questionTwo')}
                className="question-three-back-button back-button"
              ></button>
            )}
          </>
        )}
      </div>
      <div className="launchpad-sidebar">
        {inputs.brand_name && (
          <div className="project-notes">
            {inputs.brand_name && <p>{inputs.brand_name}</p>}
            {inputs.celeb_name && <p>{inputs.celeb_name}</p>}
            {inputs.context && <p>{inputs.context}</p>}
            {inputs.idea && <p>{inputs.idea}</p>}
          </div>
        )}
      </div>
      <FuseSlider />
    </div>
  );
};

export default Launchpad;
